import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/dropdown';
import { Divider } from '@heroui/divider';
import { NextUI } from '@/components/NextUI';
import Icon from '@/components/Common/Icons/Icon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

const loadGoogleFonts = () => {
  const link = document.createElement('link');
  link.href =
    'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Caveat&family=Dancing+Script&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

const fonts = [
  { name: 'Signature', value: 'Great Vibes' },
  { name: 'Handwriting', value: 'Caveat' },
  { name: 'Cursive', value: 'Dancing Script' },
];

const TypeIt = ({
  signRef,
  onSignatureChange,
}: {
  signRef?: React.MutableRefObject<HTMLCanvasElement>;
  onSignatureChange: (hasContent: boolean) => void;
}) => {
  const [text, setText] = useState('');
  const [color, setColor] = useState('#4D5DEB');
  const [selectedFont, setSelectedFont] = useState(fonts[0]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations('modals.your_sign');

  useEffect(() => {
    loadGoogleFonts();
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (signRef && canvasRef.current) {
      signRef.current = canvasRef.current;
    }
  }, [signRef]);

  useEffect(() => {
    onSignatureChange(text.trim().length > 0);
  }, [text, onSignatureChange]);

  const calculateFontSize = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    initialSize: number = 110
  ) => {
    let fontSize = initialSize;
    ctx.font = `${fontSize}px "${selectedFont.value}"`;

    let textWidth = ctx.measureText(text).width;

    while (textWidth > maxWidth * 0.9 && fontSize > 12) {
      fontSize--;
      ctx.font = `${fontSize}px "${selectedFont.value}"`;
      textWidth = ctx.measureText(text).width;
    }

    return fontSize;
  };

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const displayText = text || '';
      if (!displayText) {
        onSignatureChange(false);
        return;
      }

      const fontSize = calculateFontSize(ctx, displayText, canvas.width);
      ctx.font = `${fontSize}px "${selectedFont.value}"`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillText(displayText, canvas.width / 2, canvas.height / 2);

      onSignatureChange(displayText.trim().length > 0);
    }
  }, [text, color, selectedFont, onSignatureChange]);

  const handleClear = () => {
    setText('');
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    onSignatureChange(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);
    onSignatureChange(newText.trim().length > 0);
  };

  return (
    <div className="flex flex-col w-full py-4 md:pl-6">
      <div className="flex justify-between w-full">
        <div className="flex gap-4">
          <Dropdown className="!min-w-0">
            <DropdownTrigger>
              <NextUI.Button variant={'ghost'} className="!border-0">
                <div
                  className="rounded-full w-4 h-4"
                  style={{ backgroundColor: color }}
                ></div>
                <FontAwesomeIcon
                  icon={['fas', 'chevron-down']}
                  className="text-xs text-gray-medium"
                />
              </NextUI.Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Signature Colors">
              <DropdownItem key="blue" onClick={() => setColor('#4D5DEB')}>
                <div className="bg-[#4D5DEB] rounded-full w-4 h-4"></div>
              </DropdownItem>
              <DropdownItem key="red" onClick={() => setColor('#FF0000')}>
                <div className="bg-[#FF0000] rounded-full w-4 h-4"></div>
              </DropdownItem>
              <DropdownItem key="black" onClick={() => setColor('#000000')}>
                <div className="bg-[#000000] rounded-full w-4 h-4"></div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>

          <Dropdown>
            <DropdownTrigger>
              <NextUI.Button variant="ghost">
                {selectedFont.name}
                <FontAwesomeIcon
                  icon={['fas', 'chevron-down']}
                  className="text-xs text-gray-medium ml-2"
                />
              </NextUI.Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Font Styles">
              {fonts.map((font) => (
                <DropdownItem
                  key={font.name}
                  onClick={() => setSelectedFont(font)}
                  style={{ fontFamily: font.value }}
                >
                  {font.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <div className="bg-primary-extra-light-blue2 h-[233px] w-full rounded-extra-lg mt-5 relative">
        <div className="absolute top-0 left-8">
          <Icon name="SignIcon" width={33} height={144} />
        </div>
        <canvas
          ref={canvasRef}
          width={578}
          height={233}
          className="bg-transparent w-full h-full rounded-t-extra-lg hidden"
        />
        <span
          className={clsx(
            'text-gray-light text-[13px] absolute bottom-20 left-1/2 transform -translate-x-1/2',
            { hidden: text }
          )}
        >
          {t('placeholders.start_typing')}
        </span>

        <div className="w-full px-8 bottom-[58px] absolute text-center">
          <Divider />
        </div>
        <div className="px-8 absolute bottom-0 w-full h-full">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleTextChange}
            className="w-full h-full bg-transparent text-start focus:outline-none pl-10"
            style={{
              fontFamily: selectedFont.value,
              color: color,
              fontSize: `${calculateFontSize(
                document.createElement('canvas').getContext('2d')!,
                text,
                538
              )}px`,
            }}
          />

          <div className="flex justify-end mt-4 mb-4 absolute bottom-0 right-8 ">
            <NextUI.Button
              variant={'link'}
              onClick={handleClear}
              className="!p-0 !px-0 !py-0 text-primary-blue !min-w-0"
            >
              {t('buttons.clear')}
            </NextUI.Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypeIt;
